import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from './api.js';
import { getTelegram } from './telegram.js';
import { typologyChatShareUrl, typologyStoryParams } from './typology-share.js';
import { AsyncState, Button, Screen } from './ui.js';

type TypologyType = {
  id: string;
  emoji: string;
  title: string;
  tagline: string;
  description: string;
  inLove: string;
  idealDate: string;
  colors: [string, string];
};

type TypologyCatalog = {
  botUsername: string;
  cardBaseUrl: string;
  questions: { id: string; text: string; options: string[] }[];
  types: TypologyType[];
};

type TypologyResult = { primary: string | null; secondary: string | null; showOnProfile: boolean };

export function TypologyPage() {
  const queryClient = useQueryClient();
  const catalog = useQuery({
    queryKey: ['typology-catalog'],
    queryFn: () => api<TypologyCatalog>('/reference/typology'),
    staleTime: Infinity,
  });
  const own = useQuery({
    queryKey: ['typology-own'],
    queryFn: () => api<{ result: TypologyResult | null }>('/me/typology'),
  });
  const [answers, setAnswers] = useState<number[]>([]);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: (values: number[]) =>
      api<{ result: TypologyResult }>('/me/typology', {
        method: 'POST',
        body: JSON.stringify({ answers: values }),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['typology-own'], data);
      setStarted(false);
      setAnswers([]);
    },
    onError: (reason: unknown) =>
      setError(
        reason instanceof ApiClientError ? reason.message : 'Не удалось сохранить результат',
      ),
  });
  const visibility = useMutation({
    mutationFn: (showOnProfile: boolean) =>
      api<{ result: TypologyResult }>('/me/typology/visibility', {
        method: 'PUT',
        body: JSON.stringify({ showOnProfile }),
      }),
    onSuccess: (data) => queryClient.setQueryData(['typology-own'], data),
    onError: (reason: unknown) =>
      setError(reason instanceof ApiClientError ? reason.message : 'Не удалось изменить настройку'),
  });

  if (catalog.isLoading || own.isLoading)
    return (
      <Screen title="Мой типаж">
        <AsyncState kind="loading" />
      </Screen>
    );
  if (catalog.isError || own.isError || !catalog.data)
    return (
      <Screen title="Мой типаж">
        <AsyncState kind="error" />
      </Screen>
    );

  const data = catalog.data;
  const result = own.data?.result;
  const resultType = data.types.find((type) => type.id === result?.primary);

  if (result && resultType && !started) {
    const secondary = data.types.find((type) => type.id === result.secondary);
    const shareInput = {
      botUsername: data.botUsername,
      cardBaseUrl: data.cardBaseUrl,
      typeId: resultType.id,
      title: resultType.title,
      tagline: resultType.tagline,
    };
    const telegram = getTelegram();
    return (
      <Screen title="Мой типаж" eyebrow="Результат теста">
        <article
          className="typology-card"
          style={{
            background: `linear-gradient(160deg, ${resultType.colors[0]}, ${resultType.colors[1]})`,
          }}
        >
          <div className="typology-card__emoji" aria-hidden="true">
            {resultType.emoji}
          </div>
          <h2>{resultType.title}</h2>
          <p className="typology-card__tagline">{resultType.tagline}</p>
          {secondary && <p className="typology-card__note">С нотками: {secondary.title}</p>}
        </article>
        <section className="typology-text">
          <p>{resultType.description}</p>
          <h3>В любви</h3>
          <p>{resultType.inLove}</p>
          <h3>Идеальное свидание</h3>
          <p>{resultType.idealDate}</p>
        </section>
        <div className="typology-actions">
          {telegram?.shareToStory && (
            <Button
              onClick={() => {
                const story = typologyStoryParams(shareInput);
                telegram.shareToStory?.(story.mediaUrl, story.params);
              }}
            >
              Поделиться в истории
            </Button>
          )}
          <Button
            tone="secondary"
            onClick={() => {
              const url = typologyChatShareUrl(shareInput);
              if (telegram) telegram.openTelegramLink(url);
              else window.open(url, '_blank', 'noopener');
            }}
          >
            Отправить другу в чат
          </Button>
        </div>
        <label className="typology-toggle">
          <input
            type="checkbox"
            checked={result.showOnProfile}
            disabled={visibility.isPending}
            onChange={(event) => {
              setError('');
              visibility.mutate(event.target.checked);
            }}
          />
          <span>Показывать мой типаж в анкете</span>
        </label>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <Button
          tone="ghost"
          onClick={() => {
            setStarted(true);
            setAnswers([]);
            setError('');
          }}
        >
          Пройти заново
        </Button>
      </Screen>
    );
  }

  if (!started && !result)
    return (
      <Screen title="Мой типаж" eyebrow="Быстрый тест · 8 вопросов">
        <article className="typology-intro">
          <div className="typology-card__emoji" aria-hidden="true">
            🔮
          </div>
          <h2>Кто твой типаж?</h2>
          <p>
            8 необычных вопросов — и мы покажем, какой человек тебя зацепит. Результатом можно
            поделиться в историях или показать в анкете.
          </p>
          <Button onClick={() => setStarted(true)}>Начать тест</Button>
        </article>
      </Screen>
    );

  const step = answers.length;
  const question = data.questions[step];
  if (!question)
    return (
      <Screen title="Мой типаж">
        {error ? (
          <>
            <p className="form-error" role="alert">
              {error}
            </p>
            <Button
              onClick={() => {
                setError('');
                save.mutate(answers);
              }}
            >
              Повторить
            </Button>
          </>
        ) : (
          <AsyncState kind="loading" />
        )}
      </Screen>
    );
  return (
    <Screen title="Мой типаж" eyebrow={`Вопрос ${step + 1} из ${data.questions.length}`}>
      <progress
        className="typology-progress"
        value={step + 1}
        max={data.questions.length}
        aria-label="Прогресс теста"
      />
      <h2 className="typology-question">{question.text}</h2>
      <div className="typology-options">
        {question.options.map((option, index) => (
          <button
            key={option}
            type="button"
            className="typology-option"
            onClick={() => {
              const next = [...answers, index];
              setAnswers(next);
              if (next.length === data.questions.length) save.mutate(next);
            }}
          >
            {option}
          </button>
        ))}
      </div>
      {step > 0 && (
        <Button tone="ghost" onClick={() => setAnswers(answers.slice(0, -1))}>
          Назад
        </Button>
      )}
    </Screen>
  );
}
